/**
 * The Unit marketing profile as one editable draft (strings for inputs), shared by every field group
 * in MarketingEditor. One draft, one save: description, details and amenities are persisted together.
 */

import type { UnitMarketingProfile } from '../../types/unit-marketing.types';

export const RESTRICTION_KEYS = [
  'seniorHousing',
  'studentHousing',
  'militaryHousing',
  'disabledHousing',
  'incomeRestricted',
] as const;
export type RestrictionKey = (typeof RESTRICTION_KEYS)[number];

type Attributes = NonNullable<UnitMarketingProfile['attributes']>;

/** Parking types that also take a space count and an "included in rent" flag. */
export const PARKING_TYPES_WITH_EXTRAS: ReadonlySet<string> = new Set(['garage', 'covered', 'carport', 'driveway']);

export interface MarketingDraft {
  description: string;
  propertyType: string;
  yearBuilt: string;
  floorLevel: string;
  floorCovering: string;
  virtualTourUrl: string;
  heating: string;
  cooling: string;
  laundry: string;
  heatingFuel: string;
  parkingType: string;
  parkingSpaces: string;
  parkingIncluded: boolean;
  amenities: string[];
  restrictions: Record<RestrictionKey, boolean>;
}

export function profileToDraft(profile: UnitMarketingProfile): MarketingDraft {
  const attrs = profile.attributes ?? {};
  const parking = attrs.parking ?? {};
  const restrictions = attrs.restrictions ?? {};
  return {
    description: profile.description ?? '',
    propertyType: profile.propertyType ?? '',
    yearBuilt: attrs.yearBuilt?.toString() ?? '',
    floorLevel: attrs.floorLevel?.toString() ?? '',
    floorCovering: attrs.floorCovering ?? '',
    virtualTourUrl: profile.virtualTourUrl ?? '',
    heating: attrs.heating ?? '',
    cooling: attrs.cooling ?? '',
    laundry: attrs.laundry ?? '',
    heatingFuel: attrs.heatingFuel ?? '',
    parkingType: parking.type ?? '',
    parkingSpaces: parking.spaces?.toString() ?? '',
    parkingIncluded: parking.included ?? false,
    amenities: [...profile.amenities],
    restrictions: Object.fromEntries(RESTRICTION_KEYS.map((key) => [key, restrictions[key] ?? false])) as Record<
      RestrictionKey,
      boolean
    >,
  };
}

/** Canonical form: what is sent and what "unsaved" is measured against. */
export function normalizeDraft(draft: MarketingDraft): MarketingDraft {
  const extras = PARKING_TYPES_WITH_EXTRAS.has(draft.parkingType);
  return {
    ...draft,
    description: draft.description.trim(),
    virtualTourUrl: draft.virtualTourUrl.trim(),
    amenities: [...new Set(draft.amenities)].sort(),
    parkingSpaces: extras ? draft.parkingSpaces : '',
    parkingIncluded: extras ? draft.parkingIncluded : false,
  };
}

export function isDraftDirty(draft: MarketingDraft, baseline: MarketingDraft): boolean {
  return JSON.stringify(normalizeDraft(draft)) !== JSON.stringify(normalizeDraft(baseline));
}

/** Inline validation message for the draft, or null when it can be saved. */
export function validateDraft(draft: MarketingDraft): { virtualTourUrl?: string } | null {
  const url = draft.virtualTourUrl.trim();
  if (url) {
    try {
      new URL(url);
    } catch {
      return { virtualTourUrl: 'Please enter a valid URL (e.g. https://example.com/tour)' };
    }
  }
  return null;
}

function optionalInt(value: string): number | undefined {
  return value ? parseInt(value, 10) : undefined;
}

/** The PUT body for a draft, keeping any attribute keys this editor does not manage. */
export function draftToUpdates(draft: MarketingDraft, profile: UnitMarketingProfile): Partial<UnitMarketingProfile> {
  const d = normalizeDraft(draft);
  const managed = { ...(profile.attributes ?? {}) } as Attributes;
  for (const key of [
    'yearBuilt',
    'floorLevel',
    'floorCovering',
    'heating',
    'cooling',
    'laundry',
    'heatingFuel',
    'parking',
    'restrictions',
  ] as const) {
    delete managed[key];
  }
  const restrictions = Object.fromEntries(RESTRICTION_KEYS.filter((key) => d.restrictions[key]).map((key) => [key, true]));
  const extras = PARKING_TYPES_WITH_EXTRAS.has(d.parkingType);
  const attributes: Attributes = {
    ...managed,
    ...(d.yearBuilt ? { yearBuilt: optionalInt(d.yearBuilt) } : {}),
    ...(d.floorLevel ? { floorLevel: optionalInt(d.floorLevel) } : {}),
    ...(d.floorCovering ? { floorCovering: d.floorCovering as NonNullable<Attributes['floorCovering']> } : {}),
    ...(d.heating ? { heating: d.heating as NonNullable<Attributes['heating']> } : {}),
    ...(d.cooling ? { cooling: d.cooling as NonNullable<Attributes['cooling']> } : {}),
    ...(d.laundry ? { laundry: d.laundry as NonNullable<Attributes['laundry']> } : {}),
    ...(d.heatingFuel ? { heatingFuel: d.heatingFuel as NonNullable<Attributes['heatingFuel']> } : {}),
    ...(d.parkingType
      ? {
          parking: {
            type: d.parkingType as NonNullable<NonNullable<Attributes['parking']>['type']>,
            ...(extras && d.parkingSpaces ? { spaces: parseInt(d.parkingSpaces, 10) } : {}),
            ...(extras ? { included: d.parkingIncluded } : {}),
          },
        }
      : {}),
    ...(Object.keys(restrictions).length > 0 ? { restrictions } : {}),
  };
  return {
    description: d.description || null,
    propertyType: d.propertyType || null,
    virtualTourUrl: d.virtualTourUrl || null,
    amenities: d.amenities,
    attributes,
  };
}
